/**
 * AI Integration Card — tenant admin configures the LLM provider key + org context
 * shown to the AI prompt builder.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Switch } from '@headlessui/react';
import { clsx } from 'clsx';
import { toast } from 'react-hot-toast';
import { Sparkles, Save, Trash2, ShieldCheck, AlertTriangle, Eye, EyeOff, CheckCircle2, KeyRound } from 'lucide-react';
import { Card, Button, Input, LoadingSpinner } from '../common';
import {
  useAIConfig,
  useUpdateAIConfig,
  useRemoveAIConfig,
  useTestAIConnection,
  useUpdateOrgProfile,
} from '../../hooks/useAIConfig';
import type { AIProviderName } from '../../services/aiConfig.service';
import type { Organization } from '../../types';

interface Props {
  organization?: Organization | null;
}

interface OrgProfileFormState {
  industry: string;
  businessModel: string;
  companyDescription: string;
}

const providerLabel = (p: AIProviderName) =>
  p === 'anthropic' ? 'Anthropic (Claude)' : 'OpenAI (GPT)';

export const AIIntegrationCard: React.FC<Props> = ({ organization }) => {
  const { data: aiConfig, isLoading } = useAIConfig();
  const updateAIConfig = useUpdateAIConfig();
  const removeAIConfig = useRemoveAIConfig();
  const testConnection = useTestAIConnection();
  const updateOrgProfile = useUpdateOrgProfile();

  const [provider, setProvider] = useState<AIProviderName>('anthropic');
  const [model, setModel] = useState<string>('');
  const [apiKey, setApiKey] = useState<string>('');
  const [showKey, setShowKey] = useState(false);
  const [replacingKey, setReplacingKey] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [dailyTokenCap, setDailyTokenCap] = useState<string>('');

  const [orgProfile, setOrgProfile] = useState<OrgProfileFormState>({
    industry: '',
    businessModel: '',
    companyDescription: '',
  });

  // Hydrate form when data loads
  useEffect(() => {
    if (aiConfig) {
      setProvider(aiConfig.provider ?? 'anthropic');
      setModel(aiConfig.model ?? '');
      setEnabled(aiConfig.enabled);
      setDailyTokenCap(aiConfig.dailyTokenCap ? String(aiConfig.dailyTokenCap) : '');
    }
  }, [aiConfig]);

  useEffect(() => {
    const o = organization as (Organization & { industry?: string; businessModel?: string; companyDescription?: string }) | null | undefined;
    if (o) {
      setOrgProfile({
        industry: o.industry || '',
        businessModel: o.businessModel || '',
        companyDescription: o.companyDescription || '',
      });
    }
  }, [organization]);

  const modelsForProvider = useMemo(() => {
    if (!aiConfig?.supportedModels) return [];
    return aiConfig.supportedModels[provider] || [];
  }, [aiConfig?.supportedModels, provider]);

  // Default model when provider changes
  useEffect(() => {
    if (modelsForProvider.length > 0 && !modelsForProvider.find((m) => m.id === model)) {
      setModel(modelsForProvider[0].id);
    }
  }, [modelsForProvider, model]);

  const providerChanged = Boolean(aiConfig?.provider && aiConfig.provider !== provider);
  const needsKeyForSave = !aiConfig?.hasKey || providerChanged || replacingKey;

  const handleSave = async () => {
    try {
      const payload: Record<string, unknown> = { provider };
      if (model.trim()) payload.model = model.trim();
      if (apiKey.trim()) payload.apiKey = apiKey.trim();
      if (dailyTokenCap.trim()) payload.dailyTokenCap = Number(dailyTokenCap);
      // enabled flag — only set if we have (or are providing) a key
      if (aiConfig?.hasKey || apiKey.trim()) payload.enabled = enabled;
      await updateAIConfig.mutateAsync(payload);
      setApiKey('');
      setShowKey(false);
      setReplacingKey(false);
    } catch {
      /* toast handled in hook */
    }
  };

  const handleTest = async () => {
    try {
      const input: { provider?: AIProviderName; apiKey?: string; model?: string } = {};
      if (apiKey.trim()) {
        input.provider = provider;
        input.apiKey = apiKey.trim();
        if (model.trim()) input.model = model.trim();
      }
      const result = await testConnection.mutateAsync(
        Object.keys(input).length > 0 ? input : undefined
      );
      toast.success(
        `Connected to ${providerLabel(result.provider)} · ${result.model} (${result.latencyMs} ms)`
      );
    } catch (err) {
      const e = err as { response?: { data?: { message?: string; code?: string } }; message?: string };
      const message = e?.response?.data?.message || e?.message || 'Connection failed';
      toast.error(message);
    }
  };

  const handleRemove = async () => {
    if (!confirm('Remove AI configuration? This will disable all AI features for your organization.')) {
      return;
    }
    try {
      await removeAIConfig.mutateAsync();
      setApiKey('');
      setEnabled(false);
      setReplacingKey(false);
    } catch {
      /* toast handled */
    }
  };

  const handleSaveOrgProfile = async () => {
    try {
      await updateOrgProfile.mutateAsync({
        industry: orgProfile.industry || null,
        businessModel: orgProfile.businessModel || null,
        companyDescription: orgProfile.companyDescription || null,
      });
    } catch {
      /* toast handled */
    }
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex justify-center"><LoadingSpinner /></div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: '#EEE6FA' }}>
            <Sparkles className="w-6 h-6" style={{ color: '#6B35D4' }} />
          </div>
          <div>
            <h3 className="font-semibold text-stone-900" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
              AI Integration
            </h3>
            <p className="text-sm text-stone-500">
              Plug in your LLM provider key to enable the AI Coach, inline hints, and check-in
              assessments. Your organization pays for tokens via your own provider account.
            </p>
          </div>
        </div>
        {aiConfig?.hasKey && (
          <span
            className={clsx(
              'text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap',
              aiConfig.enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-stone-100 text-stone-500'
            )}
          >
            {aiConfig.enabled ? 'Enabled' : 'Disabled'}
          </span>
        )}
      </div>

      {/* Provider + model + key */}
      <div className="grid gap-4 sm:grid-cols-2 mb-6">
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1">Provider</label>
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value as AIProviderName)}
            className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="anthropic">Anthropic (Claude)</option>
            <option value="openai">OpenAI (GPT)</option>
          </select>
          {providerChanged && (
            <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Changing provider requires entering a new API key.
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1">Model</label>
          <select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {modelsForProvider.map((m) => (
              <option key={m.id} value={m.id}>{m.label}</option>
            ))}
          </select>
        </div>

        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-stone-700 mb-1">API Key</label>

          {aiConfig?.hasKey && !replacingKey && !providerChanged ? (
            <div className="flex items-center gap-2">
              <div className="flex-1 flex items-center gap-3 px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50/60">
                <KeyRound className="w-4 h-4 text-emerald-700 shrink-0" />
                <code className="text-sm font-mono text-stone-700 tracking-widest">
                  {'•'.repeat(20)}{aiConfig.keyLastFour}
                </code>
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full ml-auto">
                  <CheckCircle2 className="w-3 h-3" /> Key configured
                </span>
              </div>
              <Button variant="secondary" onClick={() => setReplacingKey(true)}>
                Replace key
              </Button>
              <Button
                variant="secondary"
                onClick={handleTest}
                disabled={testConnection.isPending}
              >
                {testConnection.isPending ? <LoadingSpinner size="sm" /> : (
                  <>
                    <ShieldCheck className="w-4 h-4 mr-1" /> Test
                  </>
                )}
              </Button>
            </div>
          ) : (
            <>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showKey ? 'text' : 'password'}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder={aiConfig?.hasKey ? 'Enter a new key to replace existing' : 'sk-… or your provider key'}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <button
                    type="button"
                    onClick={() => setShowKey((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700"
                    aria-label={showKey ? 'Hide key' : 'Show key'}
                  >
                    {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button
                  variant="secondary"
                  onClick={handleTest}
                  disabled={testConnection.isPending || (!aiConfig?.hasKey && !apiKey.trim())}
                >
                  {testConnection.isPending ? <LoadingSpinner size="sm" /> : (
                    <>
                      <ShieldCheck className="w-4 h-4 mr-1" /> Test
                    </>
                  )}
                </Button>
                {aiConfig?.hasKey && replacingKey && (
                  <Button
                    variant="ghost"
                    onClick={() => { setReplacingKey(false); setApiKey(''); setShowKey(false); }}
                  >
                    Cancel
                  </Button>
                )}
              </div>
              {aiConfig?.keyLastFour && replacingKey && (
                <p className="text-xs text-stone-500 mt-1">
                  Current key ends in <code className="bg-stone-100 px-1 rounded">{aiConfig.keyLastFour}</code>.
                  Submitting a new key will replace it.
                </p>
              )}
            </>
          )}

          <p className="text-xs text-stone-500 mt-1">
            Encrypted at rest with AES-256-GCM. The full key is never returned by the API.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1">
            Daily token cap (optional)
          </label>
          <Input
            type="number"
            value={dailyTokenCap}
            onChange={(e) => setDailyTokenCap(e.target.value)}
            placeholder="e.g. 200000"
            min={0}
          />
          <p className="text-xs text-stone-500 mt-1">
            Per-tenant daily ceiling on input+output tokens. Leave blank for no cap.
          </p>
        </div>

        <div className="flex items-end">
          <div className="flex items-center justify-between w-full p-3 rounded-lg bg-stone-50">
            <div>
              <div className="text-sm font-medium text-stone-900">Enable AI features</div>
              <div className="text-xs text-stone-500">
                Coach panel, inline hints, check-in assessments, chat.
              </div>
            </div>
            <Switch
              checked={enabled}
              onChange={setEnabled}
              disabled={!aiConfig?.hasKey && !apiKey.trim()}
              className={clsx(
                'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                enabled ? 'bg-primary-600' : 'bg-stone-300'
              )}
            >
              <span
                className={clsx(
                  'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                  enabled ? 'translate-x-6' : 'translate-x-1'
                )}
              />
            </Switch>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={
            updateAIConfig.isPending ||
            (needsKeyForSave && !apiKey.trim())
          }
        >
          {updateAIConfig.isPending ? <LoadingSpinner size="sm" /> : (
            <>
              <Save className="w-4 h-4 mr-1" /> Save AI configuration
            </>
          )}
        </Button>
        {aiConfig?.hasKey && (
          <Button variant="secondary" onClick={handleRemove} disabled={removeAIConfig.isPending}>
            <Trash2 className="w-4 h-4 mr-1" /> Remove key
          </Button>
        )}
      </div>

      {/* Org profile — used as AI context */}
      <div className="border-t border-stone-200 pt-6">
        <h4 className="font-medium text-stone-900 mb-1">Organization context (for AI)</h4>
        <p className="text-sm text-stone-500 mb-4">
          These fields shape how the AI reasons about your OKRs. Used in every coach call.
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Industry</label>
            <Input
              type="text"
              value={orgProfile.industry}
              onChange={(e) => setOrgProfile({ ...orgProfile, industry: e.target.value })}
              placeholder="e.g. B2B SaaS, Healthcare, Retail"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Business model</label>
            <Input
              type="text"
              value={orgProfile.businessModel}
              onChange={(e) => setOrgProfile({ ...orgProfile, businessModel: e.target.value })}
              placeholder="e.g. B2B office supplies, sales-led growth"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Company description
            </label>
            <textarea
              value={orgProfile.companyDescription}
              onChange={(e) =>
                setOrgProfile({ ...orgProfile, companyDescription: e.target.value })
              }
              placeholder="One or two sentences describing what your company does."
              rows={3}
              maxLength={2000}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        <div className="mt-4">
          <Button
            variant="secondary"
            onClick={handleSaveOrgProfile}
            disabled={updateOrgProfile.isPending}
          >
            {updateOrgProfile.isPending ? <LoadingSpinner size="sm" /> : (
              <>
                <Save className="w-4 h-4 mr-1" /> Save organization context
              </>
            )}
          </Button>
        </div>
      </div>
    </Card>
  );
};
